import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  wizardCalcSnapshotKeys,
  wizardCalcSnapshotsApi,
  type CreateCalcSnapshotTestCaseRequest,
  type ReviseCalcSnapshotTestCaseRequest,
  type RunCalcSnapshotRequest,
  type WizardCalcSnapshotRun,
} from '../api/wizard-calc-snapshots-api';

export function useWizardCalcSnapshotTestCases() {
  return useQuery({
    queryKey: wizardCalcSnapshotKeys.testCases(),
    queryFn: wizardCalcSnapshotsApi.listTestCases,
  });
}

export function useWizardCalcSnapshotDetail(
  testCaseId: string | null,
  runResultId?: string | null,
) {
  return useQuery({
    queryKey: wizardCalcSnapshotKeys.detail(testCaseId ?? '', runResultId),
    queryFn: () => wizardCalcSnapshotsApi.getTestCase(testCaseId!, runResultId),
    enabled: testCaseId != null,
  });
}

export function useWizardCalcSnapshotClientSearch(query: string) {
  return useQuery({
    queryKey: wizardCalcSnapshotKeys.clients(query),
    queryFn: () => wizardCalcSnapshotsApi.searchClients(query),
    enabled: query.length >= 2,
    staleTime: 10_000,
    retry: false,
  });
}

export function useCreateWizardCalcSnapshotTestCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCalcSnapshotTestCaseRequest) =>
      wizardCalcSnapshotsApi.createTestCase(body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wizardCalcSnapshotKeys.testCases() }),
  });
}

export function useReviseWizardCalcSnapshotTestCase(testCaseId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReviseCalcSnapshotTestCaseRequest) =>
      wizardCalcSnapshotsApi.reviseTestCase(testCaseId!, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wizardCalcSnapshotKeys.testCases() });
      if (testCaseId) {
        queryClient.invalidateQueries({ queryKey: wizardCalcSnapshotKeys.detail(testCaseId) });
      }
    },
  });
}

export function useRunWizardCalcSnapshots() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RunCalcSnapshotRequest) => wizardCalcSnapshotsApi.run(body),
    onSuccess: response => {
      queryClient.setQueryData<WizardCalcSnapshotRun[]>(wizardCalcSnapshotKeys.runs(), current => [
        { ...response.run, results: response.results },
        ...(current ?? []).filter(run => run.runId !== response.run.runId),
      ]);
      void queryClient.invalidateQueries({ queryKey: wizardCalcSnapshotKeys.runs() });
    },
  });
}

export function useWizardCalcSnapshotRuns() {
  return useQuery({
    queryKey: wizardCalcSnapshotKeys.runs(),
    queryFn: wizardCalcSnapshotsApi.listRuns,
  });
}
